import React, { useState, useEffect } from 'react';
import { apiRequest } from '../../api/api';
import { tableConfigs, tableOptions } from './tableConfigs';
import GenericPivotTable from './components/GenericPivotTable';

const MarketingBudgetTables = () => {
  const [selectedTable, setSelectedTable] = useState('ilan_reklam');
  const [tableData, setTableData] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [referenceData, setReferenceData] = useState({
    maingroups: [],
    subgroups: [],
    markets: [],
    types: []
  });


  useEffect(() => {
    fetchData();
  }, [selectedTable]);

  useEffect(() => {
    applyFilters();
  }, [selectedYear, selectedMonth, tableData, referenceData]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const currentConfig = tableConfigs[selectedTable];

      const response = await apiRequest(currentConfig.apiEndpoint);
      setTableData(response);
      setFilteredData(response);

      // Reference data'yı yükle
      const [maingroups, markets, types, countries, cities, agencies] = await Promise.all([
        apiRequest('/marketing-budget/maingroups/'),
        apiRequest('/marketing-budget/markets/'),
        apiRequest('/marketing-budget/types/'),
        apiRequest('/marketing-budget/countries/'),
        apiRequest('/marketing-budget/cities/'),
        apiRequest('/marketing-budget/agencies/')
      ]);

      // Seçili ana gruba ait alt grupları yükle
      const selectedGroupName = selectedTable === 'ilan_reklam' ? 'İlan Reklam' :
        selectedTable === 'fuar_detay' ? 'Fuar Detay' :
          selectedTable === 'promosyon' ? 'Promosyon' :
            selectedTable === 'workshop' ? 'Work Shop Road Show' :
              selectedTable === 'sales_call' ? 'Sales Call' :
                selectedTable === 'christmas_gift' ? 'Yılbaşı Hediye' :
                  selectedTable === 'web_software' ? 'Web Yazılım' :
                    selectedTable === 'repres' ? 'Yurtdışı Temsilci' :
                      selectedTable === 'represhos' ? 'Temsil Ağırlama' :
                        selectedTable === 'digital_month' ? 'Dijital Pazarlama' : '';

      let subgroups = [];
      if (selectedGroupName) {
        const selectedMaingroup = maingroups.results?.find(mg => mg.maingroup_name === selectedGroupName) ||
          maingroups.find(mg => mg.maingroup_name === selectedGroupName);
        if (selectedMaingroup) {
          subgroups = await apiRequest(`/marketing-budget/subgroups/?maingroup=${selectedMaingroup.id}`);
        }
      }

      setReferenceData({
        maingroups: maingroups.results || maingroups,
        subgroups: subgroups.results || subgroups,
        markets: markets.results || markets,
        types: types.results || types,
        countries: countries.results || countries,
        cities: cities.results || cities,
        agencies: agencies.results || agencies
      });

    } catch (error) {
      // Silently handle error
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = tableData;

    if (selectedYear) {
      filtered = filtered.filter(item => item.budget_year == selectedYear);
    }

    if (selectedMonth) {
      filtered = filtered.filter(item => item.budget_month == selectedMonth);
    }

    // Sadece girilen verileri göster - boş kombinasyonları kaldır
    setFilteredData(filtered);
  };

  // transformDataForGenericTable fonksiyonu kaldırıldı - artık sadece gerçek veriler gösteriliyor

  // Veriyi yıl ve budget type'a göre grupla
  const groupDataByYearAndBudgetType = (data) => {
    const grouped = {};

    data.forEach(item => {
      const year = item.budget_year || 'Yıl Belirtilmemiş';
      const budgetType = item.budgettype_name || 'Diğer';
      const key = `${year}`;

      if (!grouped[key]) {
        grouped[key] = {};
      }
      if (!grouped[key][budgetType]) {
        grouped[key][budgetType] = [];
      }
      grouped[key][budgetType].push(item);
    });

    return grouped;
  };

  // Global boş kombinasyonları oluştur
  // createEmptyCombinations fonksiyonu kaldırıldı - artık sadece gerçek veriler gösteriliyor
  const createEmptyCombinations_DISABLED = (data, config) => {
    const { pivotConfig } = config;
    const { rows, columns, values } = pivotConfig;

    // Dinamik olarak unique değerleri al
    const uniqueValues = {};

    // Reference data'dan unique değerleri al
    if (referenceData.markets && referenceData.markets.length > 0) {
      uniqueValues.market_name = referenceData.markets.map(m => m.budget_market_name);
    } else {
      uniqueValues.market_name = [];
    }
    if (referenceData.subgroups && referenceData.subgroups.length > 0) {
      uniqueValues.subgroup_name = referenceData.subgroups.map(s => s.subgroup_name);
    } else {
      uniqueValues.subgroup_name = [];
    }
    if (referenceData.types && referenceData.types.length > 0) {
      uniqueValues.budgettype_name = referenceData.types.map(t => t.budget_type);
    } else {
      uniqueValues.budgettype_name = [];
    }

    // Yeni alanlar için unique değerler (Fuar Detay için)
    if (referenceData.countries && referenceData.countries.length > 0) {
      uniqueValues.country = referenceData.countries.map(c => c.country_name);
    } else {
      uniqueValues.country = [];
    }

    if (referenceData.cities && referenceData.cities.length > 0) {
      uniqueValues.city = referenceData.cities.map(c => c.city_name);
    } else {
      uniqueValues.city = [];
    }

    if (referenceData.agencies && referenceData.agencies.length > 0) {
      uniqueValues.agency = referenceData.agencies.map(a => a.agency_name);
    } else {
      uniqueValues.agency = [];
    }

    // Tarih alanları için boş değerler (dinamik olarak girilecek)
    uniqueValues.start_date = ['Tarih Seç'];
    uniqueValues.end_date = ['Tarih Seç'];

    const allCombinations = [];

    // Tüm kombinasyonları oluştur
    const generateCombinations = (currentCombination, remainingFields, fieldIndex) => {
      if (fieldIndex >= remainingFields.length) {
        // Bu kombinasyonun verisi var mı kontrol et
        const existingData = data.find(item => {
          return remainingFields.every(field => item[field] === currentCombination[field]);
        });

        if (existingData) {
          // Mevcut veriyi ekle
          allCombinations.push(existingData);
        } else {
          // Boş kombinasyon oluştur
          const emptyCombination = {
            id: `empty-${Object.values(currentCombination).join('-')}`,
            ...currentCombination,
            [values]: 0,
            budget_year: null,
            budget_month: null,
            maingroup_name: selectedTable === 'ilan_reklam' ? 'İlan Reklam' :
              selectedTable === 'fuar_detay' ? 'Fuar Detay' :
                selectedTable === 'promosyon' ? 'Promosyon' : '',
            country: '',
            city: '',
            agency: '',
            is_empty: true
          };
          allCombinations.push(emptyCombination);
        }
        return;
      }

      const currentField = remainingFields[fieldIndex];
      const fieldValues = uniqueValues[currentField];

      if (fieldValues && Array.isArray(fieldValues)) {
        fieldValues.forEach(value => {
          generateCombinations(
            { ...currentCombination, [currentField]: value },
            remainingFields,
            fieldIndex + 1
          );
        });
      }
    };

    // Tüm alanları birleştir
    const allFields = [...new Set([...rows, ...columns, 'budgettype_name'])];

    // Tüm alanlar için değer var mı kontrol et
    const hasAllFields = allFields.every(field =>
      uniqueValues[field] && uniqueValues[field].length > 0
    );

    if (hasAllFields) {
      generateCombinations({}, allFields, 0);
    }

    return allCombinations;
  };

  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
  };

  const handleMonthChange = (e) => {
    setSelectedMonth(e.target.value);
  };

  const clearFilters = () => {
    setSelectedYear('');
    setSelectedMonth('');
    setFilteredData(tableData);
  };

  const currentConfig = tableConfigs[selectedTable];

  useEffect(() => {
    // Global yükleme sinyali gönder
    window.dispatchEvent(new CustomEvent('app_loading', { detail: loading }));
  }, [loading]);

  return (
    <div className="px-3 py-4 sm:px-4 md:p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Marketing Budget Table
        </h1>
      </div>

      {/* Table Selection and Filters */}
      <div className="mb-4 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 p-3">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-600 dark:text-gray-400">Tablo:</span>
            <select
              value={selectedTable}
              onChange={(e) => setSelectedTable(e.target.value)}
              className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            >
              {tableOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-600 dark:text-gray-400">Yıl:</span>
            <select
              value={selectedYear}
              onChange={handleYearChange}
              className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="">Tüm Yıllar</option>
              {Array.from(new Set(tableData.map(item => item.budget_year).filter(Boolean))).sort().map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-600 dark:text-gray-400">Ay:</span>
            <select
              value={selectedMonth}
              onChange={handleMonthChange}
              className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="">Tüm Aylar</option>
              {Array.from(new Set(tableData.map(item => item.budget_month).filter(Boolean))).sort((a, b) => a - b).map(month => (
                <option key={month} value={month}>
                  {new Date(2024, month - 1).toLocaleDateString('tr-TR', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>

          {(selectedYear || selectedMonth) && (
            <button
              onClick={clearFilters}
              className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
            >
              Temizle
            </button>
          )}
        </div>
      </div>


      {/* Yıl ve Budget Type'a Göre Ayrı Tablolar */}
      {currentConfig && (() => {
        const groupedData = groupDataByYearAndBudgetType(filteredData);
        const years = Object.keys(groupedData).sort((a, b) => {
          // "Yıl Belirtilmemiş" en alta
          if (a === 'Yıl Belirtilmemiş') return 1;
          if (b === 'Yıl Belirtilmemiş') return -1;
          // Yılları büyükten küçüğe sırala (en yeni en üstte)
          return parseInt(b) - parseInt(a);
        });

        if (years.length === 0) {
          return (
            <div className="text-center py-12">
              <div className="text-gray-500 dark:text-gray-400">
                <svg className="mx-auto h-12 w-12 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-lg font-medium">Henüz bütçe verisi bulunmuyor</p>
                <p className="text-sm">Yeni bütçe kaydı oluşturmak için Budget sayfasını kullanın</p>
              </div>
            </div>
          );
        }

        return years.map(year => {
          const budgetTypes = Object.keys(groupedData[year]);

          return (
            <div key={year} className="mb-12">
              {/* Yıl Başlığı */}
              <div className="mb-6 pb-3 border-b-2 border-blue-500 dark:border-blue-400">
                <h2 className="text-3xl font-bold text-blue-600 dark:text-blue-400 flex items-center">
                  <svg className="w-8 h-8 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {year}
                </h2>
              </div>

              {/* Budget Type'a Göre Tablolar */}
              {budgetTypes.map(budgetType => (
                <div key={`${year}-${budgetType}`} className="mb-8">
                  <GenericPivotTable
                    config={{
                      ...currentConfig,
                      title: `${year} - ${budgetType} - ${currentConfig.title}`
                    }}
                    data={groupedData[year][budgetType]}
                    referenceData={referenceData}
                  />
                </div>
              ))}
            </div>
          );
        });
      })()}

    </div>
  );
};

export default MarketingBudgetTables;
